/**
 * routes/Overview — the daily-driver dashboard (TF.3). Reads the shell's real-time feed
 * (equity, daily P&L, the daily-loss governor, open positions) and adds its own two
 * reads: the windowed equity curve (hero chart) and the committee decision feed. Layout
 * is mobile-first — every section is full-width and stacks on a phone, upgrading to the
 * two-column split on wide screens.
 */
import { useEffect, useMemo, useState } from 'react';

import { api } from '../api/client';
import type { DecisionRecord, DecisionsResponse, EquityCurveResponse, EquityRange, LivePosition } from '../api/types';
import { usePoll } from '../api/usePoll';
import { useShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { DailyLossMeter } from '../components/DailyLossMeter';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { StatTile } from '../components/StatTile';
import { EquityChart } from '../components/charts/EquityChart';
import { DecisionItem } from '../components/committee/DecisionItem';
import { ago, directionClass, num, pct, pctPlain, signedMoney } from '../lib/format';

const RANGES: readonly { key: EquityRange; label: string }[] = [
  { key: '1d', label: '1D' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: 'all', label: 'All' },
];

const POSITION_COLUMNS: readonly Column<LivePosition>[] = [
  { key: 'symbol', header: 'Symbol', cell: (p) => <strong>{p.symbol}</strong> },
  {
    key: 'side',
    header: 'Side',
    cell: (p) => <span className={`side side--${p.side.toLowerCase()}`}>{p.side}</span>,
  },
  { key: 'qty', header: 'Size', align: 'right', cell: (p) => <span className="num">{num(p.qty, 3)}</span> },
  { key: 'entry', header: 'Entry', align: 'right', cell: (p) => <span className="num">{num(p.entryPrice)}</span> },
  { key: 'mark', header: 'Mark', align: 'right', cell: (p) => <span className="num">{num(p.markPrice)}</span> },
  { key: 'lev', header: 'Lev', align: 'right', cell: (p) => <span className="num">{p.leverage}×</span> },
  {
    key: 'sl',
    header: 'Stop',
    align: 'right',
    cell: (p) =>
      p.slArmed && p.stopPrice != null ? (
        <span className="badge badge--profit num">
          <span aria-hidden>✓</span> {num(p.stopPrice)}
        </span>
      ) : (
        <span className="badge badge--warning">⚠ none</span>
      ),
  },
  {
    key: 'pnl',
    header: 'uPnL',
    align: 'right',
    cell: (p) => (
      <span className={`num ${directionClass(p.uPnl)}`}>
        {signedMoney(p.uPnl)} <span className="muted">({pct(p.uPnlPct)})</span>
      </span>
    ),
  },
];

/** Merge the SSE-pushed latest decision into the polled list, newest-first, deduped. */
function mergeDecisions(list: readonly DecisionRecord[], pushed: DecisionRecord | null): readonly DecisionRecord[] {
  if (!pushed) return list;
  if (list.some((d) => d.id === pushed.id)) return list;
  return [pushed, ...list];
}

export function Overview(): JSX.Element {
  const live = useShell();
  const [range, setRange] = useState<EquityRange>('30d');

  const curve = usePoll<EquityCurveResponse>((signal) => api.equityCurve(range, signal), { intervalMs: 30_000 });
  const decisions = usePoll<DecisionsResponse>((signal) => api.decisions(signal), { intervalMs: 15_000 });

  // Re-fetch the curve when the operator changes the range window.
  useEffect(() => { curve.refresh(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  const ov = live.overview;
  const positions = live.positions?.positions ?? [];

  const feed = useMemo(
    () => mergeDecisions(decisions.data?.decisions ?? [], live.latestDecision).slice(0, 6),
    [decisions.data, live.latestDecision],
  );

  // First paint, before any transport has delivered a snapshot.
  if (!ov && !live.positions && live.transport === 'connecting') {
    return (
      <div className="state">
        <span className="spinner" aria-hidden />
        <span>Connecting to terminal…</span>
      </div>
    );
  }

  const points = curve.data?.points ?? [];

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Overview</h1>
        <span className="page-head__sub">
          {live.updatedAt ? `updated ${ago(live.updatedAt)}` : 'connecting…'}
        </span>
      </div>

      {/* The halted state + owner re-arm control now render globally in the AppShell
          (HaltBanner), so they are visible and actionable on every page — not only here. */}

      {live.error && !ov && (
        <div className="banner-error section" role="alert">
          Live feed unavailable: {live.error}
        </div>
      )}

      {/* ── Hero: equity curve ─────────────────────────────────────────────── */}
      <Card
        title="Equity curve"
        hint={ov?.source === 'demo-seed' ? 'demo seed' : 'live'}
        actions={
          <div className="seg" role="group" aria-label="Equity curve range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`seg__btn${range === r.key ? ' is-active' : ''}`}
                aria-pressed={range === r.key}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      >
        {points.length >= 2 ? (
          <EquityChart points={points} caption="Account equity" />
        ) : curve.loading ? (
          <div className="state"><span className="spinner" aria-hidden /></div>
        ) : (
          <div className="state"><span className="muted">Not enough history to plot this range.</span></div>
        )}
      </Card>

      {/* ── Trading KPIs ───────────────────────────────────────────────────── */}
      <div className="stat-grid section">
        <StatTile
          label="Win rate"
          value={ov ? pctPlain(ov.winrate, 1) : '—'}
          sub={ov ? `${ov.wins}W · ${ov.losses}L` : undefined}
          tone={ov && ov.winrate >= 0.5 ? 'profit' : 'neutral'}
        />
        <StatTile
          label="Avg R"
          value={ov ? `${ov.avgR >= 0 ? '+' : '−'}${Math.abs(ov.avgR).toFixed(2)}R` : '—'}
          sub="per closed trade"
          tone={ov ? (ov.avgR >= 0 ? 'profit' : 'loss') : 'neutral'}
        />
        <StatTile label="Trades" value={ov ? ov.tradeCount : '—'} sub="closed, all-time" tone="neutral" />
        <StatTile
          label="Max drawdown"
          value={ov ? `−${pctPlain(ov.drawdownPct, 1)}` : '—'}
          sub="peak to trough"
          tone={ov && ov.drawdownPct > 0 ? 'warning' : 'neutral'}
        />
      </div>

      {/* ── Daily-loss governor ────────────────────────────────────────────── */}
      <Card title="Daily loss governor" hint="hard −2% halt" >
        {ov ? (
          <DailyLossMeter daily={ov.dailyLoss} />
        ) : (
          <div className="state"><span className="spinner" aria-hidden /></div>
        )}
      </Card>

      {/* ── Positions + committee ──────────────────────────────────────────── */}
      <div className="grid-2 section">
        <Card title="Open positions" hint={`${positions.length} open`} flush>
          <DataTable
            columns={POSITION_COLUMNS}
            rows={positions}
            rowKey={(p) => p.symbol}
            empty="No open positions."
          />
        </Card>

        <Card title="Committee feed" hint={`${feed.length} recent`}>
          {decisions.loading && !decisions.data ? (
            <div className="state"><span className="spinner" aria-hidden /></div>
          ) : feed.length === 0 ? (
            <div className="state"><span className="muted">No decisions yet.</span></div>
          ) : (
            <ul className="decision-list">
              {feed.map((d) => (
                <DecisionItem key={d.id} decision={d} />
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
